import type { SalesChecklistSection } from "@expert/contentful";
import { Accordion, Flex, Stack, Text } from "@mantine/core";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { ChecklistItem } from "@/sales-tools/home-product/components/FullOfferChecklist/components/ChecklistItem";
import { CoverageCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/CoverageCarousel";
import { EngagementCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/EngagementCarousel";
import { RapportCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/RapportCarousel";
import { isSectionComplete } from "@/sales-tools/home-product/components/FullOfferChecklist/utils/isSectionComplete";
import { GreenCheckMarkFilled } from "@/sales-tools/home-product/components/Icons";
import { useOfferChecklistStore } from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useSessionId } from "@/sdk";

export function ChecklistSection({
    section,
    onSectionComplete,
}: {
    section: SalesChecklistSection;
    onSectionComplete: (title: string) => void;
}): JSX.Element | null {
    const sessionId = useSessionId();
    const data = useOfferChecklistStore((state) => state.getData(sessionId));
    const isCompleted = isSectionComplete(section.checklistItems, data);
    const [shouldShowDoneIcon, setShouldShowDoneIcon] = useState(false);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (isCompleted && !shouldShowDoneIcon) {
            onSectionComplete(section.title);
            timeoutId = setTimeout(() => {
                setShouldShowDoneIcon(true);
            }, 500);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isCompleted, onSectionComplete, section.title, shouldShowDoneIcon]);

    return (
        <>
            <Accordion.Item key={section.title} value={section.title}>
                <Accordion.Control
                    w={isCompleted ? "90%" : undefined}
                    disabled={isCompleted}
                    styles={{
                        control: { opacity: 1 },
                        label: isCompleted ? { opacity: 0.4 } : undefined,
                    }}
                    chevron={
                        shouldShowDoneIcon ? (
                            <Flex align="center" gap={4}>
                                <GreenCheckMarkFilled />
                                <Text size="xs" c="dark.2">
                                    Done
                                </Text>
                            </Flex>
                        ) : undefined
                    }
                >
                    <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                        {section.title}
                    </Text>
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap="xs">
                        {section.checklistItems.map((item) => (
                            <Stack gap={0} key={item.phrase}>
                                <ChecklistItem item={item} />
                                {item.key === "RAPPORT" && <RapportCarousel defaultExample={item.defaultExample} />}
                                {item.key === "COVERAGE" && <CoverageCarousel defaultExample={item.defaultExample} />}
                                {item.key === "ENGAGEMENT_QUESTION" && <EngagementCarousel examples={item.examples} />}
                            </Stack>
                        ))}
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </>
    );
}
